/**
 * Working-day helpers aligned with `utils/dueDateCalculator.js` on the server:
 * inclusive Mon–Fri counts in UTC, `YYYY-MM-DD` parsed to UTC noon (no ms-span math).
 * Optional `holidaySet` ('YYYY-MM-DD' keys) skips additional non-working days when provided.
 */

/**
 * @param {Date|string} date
 * @returns {Date}
 */
export function parseDate(date) {
  const d = new Date(date);
  d.setUTCHours(12, 0, 0, 0);
  return d;
}

/**
 * @param {Date} d
 * @returns {string}
 */
export function getDateKey(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * The user's local calendar day, as a UTC-noon Date (pairs with ISO
 * date-only strings). Uses local date parts: the UTC date is already
 * tomorrow on US evenings.
 * @returns {Date}
 */
export function utcCalendarTodayNoon() {
  const n = new Date();
  return new Date(
    Date.UTC(n.getFullYear(), n.getMonth(), n.getDate(), 12, 0, 0, 0)
  );
}

/**
 * Count working days (Mon–Fri, optional holidays) from startDate through endDate inclusive.
 * @param {Date|string|null|undefined} startDate
 * @param {Date|string|null|undefined} endDate
 * @param {Set<string>|null|undefined} holidaySet
 * @returns {number}
 */
export function countWorkingDays(startDate, endDate, holidaySet = null) {
  if (
    startDate == null ||
    startDate === '' ||
    endDate == null ||
    endDate === ''
  ) {
    return 0;
  }
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 0;
  }
  if (start.getTime() > end.getTime()) {
    return 0;
  }

  let count = 0;
  const cursor = new Date(start.getTime());

  while (cursor.getTime() <= end.getTime()) {
    const dow = cursor.getUTCDay();
    const isWeekend = dow === 0 || dow === 6;
    const isHoliday =
      holidaySet && holidaySet.size > 0 && holidaySet.has(getDateKey(cursor));
    if (!isWeekend && !isHoliday) {
      count++;
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return count;
}
